import {envIsDev} from '../../config';
import type {RunKeySection} from '../../util/runTypes';
import {makeTrackEvent} from './makeTrackEvent';
import type {NamedWorkspaceType} from './workspacesEvents';

export type WorkspaceRunSelectorInventoryEvent = {
  schemaVersion: number;
  entityName: string;
  projectName: string;
  viewId?: string;
  runSetId: string;
  viewName: string;
  viewType: string;
  workspaceType: NamedWorkspaceType;
  numActiveFilters: number;
  numInactiveFilters: number;
  filterKeySections: RunKeySection[];
  numActiveFilterGroups: number;
  numInactiveFilterGroups: number;
  numGroupingKeys: number;
  groupKeySections: RunKeySection[];
  hasCustomSort: boolean;
  numSortKeys: number;
  sortKeySections: RunKeySection[];
  hasRunVisibilityOverride: boolean;
  numPinnedRuns: number;
  hasBaselineRun: boolean;
  numPinnedColumns: number;
  pinnedColumnKeySections: RunKeySection[];
};

export const trackWorkspaceRunSelectorInventory = makeTrackEvent<
  WorkspaceRunSelectorInventoryEvent,
  {
    schemaVersion: {
      description: 'version of this payload shape, bumped when a property is added, removed, or changes meaning';
      exampleValues: [1];
    };
    entityName: {
      description: 'entity that owns the workspace project';
      exampleValues: ['team-hugh'];
    };
    projectName: {
      description: 'project the workspace belongs to';
      exampleValues: ['panel-sandbox'];
    };
    viewId: {
      description: 'server ID of the view, absent if the view has not been saved yet';
      exampleValues: ['VmlldzoxMjM0NTY3'];
    };
    runSetId: {
      description: 'client-side run-set ID used to correlate events for the same run selector';
      exampleValues: ['run-set-1'];
    };
    viewName: {
      description: 'the name field in the views table';
      exampleValues: ['nw-abc123-v', 'workspace'];
    };
    viewType: {
      description: 'the type field in the views table';
      exampleValues: ['project-view', 'sweep-view', 'group-view'];
    };
    workspaceType: {
      description: 'whether the workspace is personal, shared, legacy, or unknown';
      exampleValues: [
        'personal',
        'shared',
        'legacy personal',
        'legacy shared',
        'unknown',
      ];
    };
    numActiveFilters: {
      description: 'number of enabled non-placeholder run filter conditions';
    };
    numInactiveFilters: {
      description: 'number of disabled non-placeholder run filter conditions, including conditions inside a disabled group';
    };
    filterKeySections: {
      description: 'sorted distinct run-key sections used by active and inactive filter conditions';
      exampleValues: [['config', 'run']];
    };
    numActiveFilterGroups: {
      description: 'number of enabled nested filter groups whose ancestors are enabled; the root is excluded';
    };
    numInactiveFilterGroups: {
      description: 'number of disabled nested filter groups or groups inside a disabled ancestor; the root is excluded';
    };
    numGroupingKeys: {
      description: 'number of run-selector grouping keys, excluding the group-all sentinel';
    };
    groupKeySections: {
      description: 'sorted distinct run-key sections used by grouping keys, excluding the group-all sentinel';
      exampleValues: [['config', 'run']];
    };
    hasCustomSort: {
      description: 'whether the sort differs from createdAt descending';
      exampleValues: [true, false];
    };
    numSortKeys: {
      description: 'number of sort keys, including the default createdAt key';
    };
    sortKeySections: {
      description: 'sorted distinct run-key sections used by sort keys';
      exampleValues: [['summary']];
    };
    hasRunVisibilityOverride: {
      description: 'whether run visibility differs from the default all-visible state in the view spec';
      exampleValues: [true, false];
    };
    numPinnedRuns: {
      description: 'number of pinned run IDs in the run-set configuration';
    };
    hasBaselineRun: {
      description: 'whether the run-set configuration has a baseline run';
      exampleValues: [true, false];
    };
    numPinnedColumns: {
      description: 'number of columns whose run-table configuration marks them pinned';
    };
    pinnedColumnKeySections: {
      description: 'sorted distinct run-key sections used by pinned columns';
      exampleValues: [['summary']];
    };
    _description: 'Run selector and run table configuration when a workspace loads';
    _location: 'multi-run, group, and sweep workspaces';
    _motivation: 'Measure how workspaces use filtering, grouping, sorting, visibility, run pinning, and column pinning';
  }
>('Workspace run selector inventory', {shouldLogToConsole: envIsDev});
